import ApiRateLimitBanner from 'components/ApiRateLimitBanner';
import RefreshFab from 'components/ui/RefreshFab';
import ConfirmActionModal from 'components/common/ConfirmActionModal';
import { PackAndFocusEditorDrawer } from 'components/pack-and-focus-editor';
import PackPreviewDrawer from 'components/pack-focus-picker/PackPreviewDrawer';
import MoreOptionsModalRevamp from 'components/setting-generator-revamp/tabs-revamp/more-options/MoreOptionsModalRevamp';
// TEMP: hide floating bot/chat FABs while validating day-plan FloatingWorkspace placement
// import FloatingChatButton from 'components/common/FloatingChatButton';
import OverlaySpinner from 'components/OverlaySpinner';
import ToastMessage from 'components/ToastMessage';
import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useNavigate } from 'react-router-dom';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { getHabitPacks } from 'store/reducer/habit-pack/extra';
import { updateShowPrivacyPolicyModal } from 'store/reducer/setting/slice';
import { fetchTeacherClasses } from 'store/reducer/class-management/extra';
import { fetchAdminTeams } from 'store/reducer/team/extra';
import { layoutSelector } from 'store/reducer/user/selectors';
import { getApiLanguageCode } from 'utils/helpers';
import {
  resumeStoredInvitations,
  STORED_INVITATION_RESUMES
} from 'utils/invitation.util';
// TEMP: restore with Zoho SalesIQ Helmet block
// import { isTrialOrPromoActive } from 'utils/subscription.util';
import { hasSchoolTeam } from 'utils/team.util';
import NavBar from '../navBar';
import Footer from './Footer';
import { isTrialOrPromoActive } from 'utils/subscription.util';

const Layout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const dummyDiv = useRef<HTMLDivElement>(null);
  const {
    userInfo,
    showConfirmModal,
    areUserDetailsLoading,
    language,
    teams,
    teamsInitialized,
    entitlements,
    isTrialUser
  } = useAppSelectorShallow(layoutSelector);

  useEffect(() => {
    dummyDiv.current && dummyDiv.current.scrollIntoView({ behavior: 'smooth' });
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getHabitPacks({
        language: getApiLanguageCode(language),
        marketplace_approval_status: true,
        is_featured: true
      })
    );
  }, [language, dispatch]);

  useEffect(() => {
    if (!userInfo?.email) {
      return;
    }
    resumeStoredInvitations({
      invitations: STORED_INVITATION_RESUMES,
      userEmail: userInfo.email,
      navigate
    });
  }, [userInfo, navigate]);

  const isPaying = useMemo(
    () => isTrialOrPromoActive(entitlements) || isTrialUser,
    [entitlements, isTrialUser]
  );

  // admin-teams?include_teaching=true is authoritative for admin/teacher access
  // (returns [] for students). Do not gate on RevenueCat entitlements — a stale
  // team_admin grant would otherwise fetch classes and show Team nav.
  useEffect(() => {
    if (userInfo?.id) {
      dispatch(fetchAdminTeams());
    }
  }, [userInfo?.id, dispatch]);

  useEffect(() => {
    if (teamsInitialized && hasSchoolTeam(teams)) {
      dispatch(fetchTeacherClasses());
    }
  }, [teamsInitialized, teams, dispatch]);

  useEffect(() => {
    if (userInfo?.id) {
      !userInfo.has_consented_to_terms_of_service &&
        dispatch(updateShowPrivacyPolicyModal(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id]);

  // TEMP disabled / hidden: do not auto-navigate new accounts into onboarding flow.
  // useEffect(() => {
  //   const webOnboardingData = localDeviceSettings.Web?.onboarding;
  //   const fromOnboarding = location.state?.fromOnboarding;
  //
  //   if (
  //     userInfo?.id &&
  //     !isCurrentPath(ROUTES.ONBOARD_FLOW) &&
  //     !fromOnboarding &&
  //     webOnboardingData &&
  //     webOnboardingData.currentStep !== ('completed' as ONBOARD_FLOW_STEP)
  //   ) {
  //     runAfterDelay(() => {
  //       navigate(ROUTES.ONBOARD_FLOW, {
  //         replace: true,
  //         state: { redirectToOnboarding: true }
  //       });
  //     }, TIMEOUT_DELAY.ONBOARDING_REDIRECT);
  //   }
  // }, [
  //   localDeviceSettings.Web,
  //   userInfo?.id,
  //   navigate,
  //   location.state?.fromOnboarding
  // ]);

  return (
    <div className='w-full h-full max-h-full flex bg-secondary-50 dark:bg-secondary-dark-100 text-secondary-dark-50 dark:text-secondary-50'>
      <NavBar />
      <div className='flex-1 flex flex-col h-full max-h-full overflow-y-auto scrollbar-thin-custom'>
        <ApiRateLimitBanner />
        <RefreshFab isInline onRefresh={() => window.location.reload()} />
        <div className='float-left clear-both' ref={dummyDiv}></div>
        <main className='w-full flex-1 ~p-4/8 flex flex-col items-center max-w-[80rem] mx-auto'>
          <Outlet />
        </main>
        <Footer />
      </div>
      {areUserDetailsLoading && (
        <OverlaySpinner
          title={t('auth_page.hang_tight_making_sure_your_info_is_up_to_date')}
        />
      )}
      {showConfirmModal && <ConfirmActionModal />}
      <PackAndFocusEditorDrawer />
      <PackPreviewDrawer />
      <MoreOptionsModalRevamp />
      <ToastMessage />
      {userInfo?.id && (
        <Helmet>
          <script>
            {`
              window.$zoho=window.$zoho || {};
              $zoho.salesiq=$zoho.salesiq||{ready:function(){    
              $zoho.salesiq.chat.messagehint("Please leave a message. We will contact you by email.");                              
              $zoho.salesiq.visitor.info["email"] = "${userInfo?.email}";
              $zoho.salesiq.visitor.info["id"] = "${userInfo?.id}";
              $zoho.salesiq.visitor.info["isPaying"] = ${isPaying} ? "Yes" : "No";}}
             `}
          </script>
          <script
            id='zsiqscript'
            src='https://salesiq.zohopublic.com.au/widget?wc=8b0f55dff01a5ffbcd3e74af14a8a7a9a20a66298fb3622b6963b8abf1f00da1'
            defer></script>
        </Helmet>
      )}
    </div>
  );
};

export default Layout;
